import { useState, useEffect } from "react";
import { Lock } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useEntitlements } from "../lib/entitlements";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { useManagerPinGate } from "../components/ManagerPinGate";
import ManagerPinSection from "../components/ManagerPinSection";
import PlanManager from "../components/PlanManager";

export default function DashSettings() {
  const { business, setBusiness } = useAuth();
  const ent = useEntitlements();
  const nav = useNavigate();

  // Same PIN-gate pattern as DashAdmin. If no PIN is set, no wall.
  const gate = useManagerPinGate();
  const [unlocked, setUnlocked] = useState(false);
  const [checkingGate, setCheckingGate] = useState(true);
  useEffect(() => {
    if (gate.pinSet === null) return;
    if (gate.pinSet === false) { setUnlocked(true); setCheckingGate(false); return; }
    (async () => {
      const ok = await gate.request("open Settings");
      setUnlocked(ok);
      setCheckingGate(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.pinSet]);
  const [form, setForm] = useState({
    business_name: business.business_name,
    average_wait_minutes: business.average_wait_minutes,
    ready_message_template: business.ready_message_template,
    address: business.address || "",
    hours: business.hours || "",
    ai_greeting: business.ai_greeting || "",
    voice_mode: business.voice_mode || "gather",
    mimi_voice: business.mimi_voice || "nova",
    directory_optin: business.directory_optin || false,
    directory_city: business.directory_city || "",
    directory_blurb: business.directory_blurb || "",
    togo_mode: business.togo_mode || "disabled",
    togo_provider: business.togo_provider || "",
    togo_url: business.togo_url || "",
    owner_phone: business.owner_phone || "",
    waitlist_manual_status: business.waitlist_manual_status || "auto",
    waitlist_capacity: business.waitlist_capacity || null,
    open_time: business.open_time || "",
    close_time: business.close_time || "",
    manager_off_hours_auto: business.manager_off_hours_auto !== false,
    emergency_closed_until: business.emergency_closed_until || "",
    standby_enabled: business.standby_enabled !== false,
    manager_phone: business.manager_phone || "",
    manager_schedule: business.manager_schedule || {},
    timezone: business.timezone || "America/New_York",
    parking: business.parking || "",
    wifi: business.wifi || "",
    dress_code: business.dress_code || "",
    kid_friendly: business.kid_friendly || "",
    dog_friendly: business.dog_friendly || "",
    wheelchair_accessible: business.wheelchair_accessible || "",
    outdoor_seating: business.outdoor_seating || "",
    dietary_options: business.dietary_options || "",
    cuisine_summary: business.cuisine_summary || "",
    menu_categories: business.menu_categories || "",
    dietary_summary: business.dietary_summary || "",
    price_range: business.price_range || "",
    payment_methods: business.payment_methods || "",
    reservations_policy: business.reservations_policy || "",
    delivery_info: business.delivery_info || "",
    happy_hour: business.happy_hour || "",
    private_events: business.private_events || "",
    tables_count: business.tables_count || "",
  });

  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await api.patch("/settings", {
        ...form,
        average_wait_minutes: parseInt(form.average_wait_minutes),
        // tables_count comes in as a string from the number input. Empty → null
        // so we don't fail Pydantic's Optional[int] coercion, otherwise parseInt.
        tables_count: form.tables_count === "" || form.tables_count == null ? null : parseInt(form.tables_count),
      });
      setBusiness(r.data);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const removeAddon = async (key) => {
    if (!confirm(`Remove ${ent.available_addons?.[key]?.name}? You can turn it back on anytime.`)) return;
    try { await api.post(`/addons/${key}/remove`); toast.success("Removed"); ent.refresh && ent.refresh(); }
    catch { toast.error("Failed"); }
  };

  const publicUrl = `${window.location.origin}/join/${business.slug}`;
  const activeAddons = ent?.addons || [];
  const available = ent?.available_addons || {};

  if (checkingGate) return <div className="max-w-2xl">{gate.modal}</div>;
  if (!unlocked) {
    return (
      <div className="max-w-2xl" data-testid="settings-locked">
        {gate.modal}
        <div className="paper-card p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-[#D96C5B] mx-auto flex items-center justify-center mb-4">
            <Lock size={26} color="#fff" weight="fill" />
          </div>
          <h1 className="text-2xl font-heading font-extrabold mb-2">Settings are PIN-protected</h1>
          <p className="text-sm text-[#5C6B61] mb-5 leading-relaxed">
            A manager PIN is set on this account. Enter it to open Settings — everything unlocks for 5 minutes so you're not retyping.
          </p>
          <button
            type="button"
            onClick={async () => { const ok = await gate.request("open Settings"); setUnlocked(ok); }}
            className="btn-primary"
            data-testid="settings-unlock-btn"
          >
            Unlock
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      {gate.modal}
      <div className="overline">Business</div>
      <h1 className="text-3xl mb-8">Settings</h1>

      <div className="paper-card p-6 mb-6" data-testid="settings-manager-pin-card">
        <ManagerPinSection />
      </div>

      <PlanManager />

      <div className="paper-card p-6 mb-6">
        <div className="border-t border-[#E1DFD5] pt-3">
          <div className="overline mb-2">Legacy active add-ons</div>
          {activeAddons.filter((k) => available[k]).length === 0 && <p className="text-sm text-[#5C6B61]">None — plan features are managed in the panel above.</p>}
          <div className="space-y-2">
            {activeAddons.filter((k) => available[k]).map((k) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span>{available[k]?.name || k} <span className="text-[#5C6B61]">· ${available[k]?.price}/mo</span></span>
                <button onClick={() => removeAddon(k)} className="text-xs text-[#A5382A] hover:underline" data-testid={`remove-addon-${k}`}>Remove</button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="paper-card p-6 mb-6">
        <div className="overline mb-2">Public join link</div>
        <div className="flex items-center gap-3">
          <code className="text-sm bg-[#E6E2D6] px-3 py-2 rounded-lg flex-1 truncate">{publicUrl}</code>
          <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }} className="btn-ghost !py-2 !px-4 text-sm" data-testid="settings-copy">Copy</button>
          <button onClick={() => nav(`/join/${business.slug}`)} className="btn-ghost !py-2 !px-4 text-sm">Open</button>
        </div>
      </div>

      <form onSubmit={save} className="paper-card p-6 space-y-4">
        <div><label className="overline block mb-1">Business name</label><input className="input-field" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="settings-name" /></div>
        <div><label className="overline block mb-1">Average wait (minutes per party)</label><input type="number" min={1} className="input-field" value={form.average_wait_minutes} onChange={(e) => setForm({ ...form, average_wait_minutes: e.target.value })} /></div>
        <div><label className="overline block mb-1">Address</label><input className="input-field" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
        <div><label className="overline block mb-1">Hours</label><input className="input-field" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} /></div>
        <div>
          <label className="overline block mb-1">Timezone</label>
          <select
            className="input-field"
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            data-testid="settings-timezone"
          >
            <option value="America/New_York">Eastern — America/New_York</option>
            <option value="America/Chicago">Central — America/Chicago</option>
            <option value="America/Denver">Mountain — America/Denver</option>
            <option value="America/Phoenix">Mountain (Arizona) — America/Phoenix</option>
            <option value="America/Los_Angeles">Pacific — America/Los_Angeles</option>
            <option value="America/Anchorage">Alaska — America/Anchorage</option>
            <option value="Pacific/Honolulu">Hawaii — Pacific/Honolulu</option>
            <option value="America/Puerto_Rico">Atlantic — America/Puerto_Rico</option>
            <option value="UTC">UTC</option>
          </select>
          <p className="text-[10px] text-[#5C6B61] mt-1">When a caller asks the AI to hold a spot for "8pm", we parse that as 8pm in this timezone. Set this to your restaurant's local time.</p>
        </div>

        <div className="border-t border-[#E1DFD5] pt-4">
          <div className="overline mb-1">Guest FAQ — what Mimi tells callers</div>
          <p className="text-xs text-[#5C6B61] leading-snug mb-3">
            Fill in whatever's true. Mimi answers <b>only</b> the fields you complete — leave one blank and she'll offer to have a team member follow up. Change any time. These are the same questions people ask on Google.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              { k: "parking", label: "Parking", ph: "Free lot behind us. Metered street parking too." },
              { k: "wifi", label: "Wi-Fi", ph: "Free Wi-Fi — password on the receipt." },
              { k: "dress_code", label: "Dress code", ph: "Smart casual. No swimwear." },
              { k: "kid_friendly", label: "Kids", ph: "Kids welcome. High chairs + kids menu." },
              { k: "dog_friendly", label: "Dogs", ph: "Well-behaved dogs on the patio only." },
              { k: "wheelchair_accessible", label: "Wheelchair access", ph: "Ramp at the main entrance. ADA restroom." },
              { k: "outdoor_seating", label: "Outdoor seating", ph: "8-table patio, dog-friendly, no reservations." },
              { k: "dietary_options", label: "Dietary options (detailed)", ph: "Gluten-free pasta, vegan pizza, nut-free available." },
              { k: "cuisine_summary", label: "Cuisine (one line)", ph: "Neighborhood Italian — wood-fired pizza and hand-made pasta." },
              { k: "menu_categories", label: "Menu categories", ph: "Pizza, pasta, salads, antipasti, desserts, wine list." },
              { k: "dietary_summary", label: "Dietary summary (Mimi speaks this verbatim)", ph: "Vegetarian + vegan options. GF pasta available. NOT a nut-free kitchen." },
              { k: "price_range", label: "Price range (Mimi quotes ranges only, never specific dishes)", ph: "Antipasti $8-16, entrees $18-38, desserts $9-12." },
              { k: "payment_methods", label: "Payment", ph: "All major cards + Apple Pay. No personal checks." },
              { k: "reservations_policy", label: "Waitlist / book-ahead policy", ph: "Walk-in only. Call ~20 min before to get on the list. We don't hold tables — we hold your spot in line." },
              { k: "delivery_info", label: "Delivery / takeout", ph: "Takeout available. Delivery via DoorDash + UberEats." },
              { k: "happy_hour", label: "Happy hour", ph: "Mon-Fri 4-6pm. $6 wines, half-off apps." },
              { k: "private_events", label: "Private events", ph: "Buyouts for 20+ available. Email events@..." },
            ].map(({ k, label, ph }) => (
              <div key={k}>
                <label className="overline block mb-1">{label}</label>
                <input
                  className="input-field !py-2 text-sm"
                  placeholder={ph}
                  value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`profile-${k}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div id="tables-count">
          <label className="overline block mb-1">Total tables in the dining room</label>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={200}
            className="input-field"
            value={form.tables_count}
            onChange={(e) => setForm({ ...form, tables_count: e.target.value })}
            data-testid="settings-tables-count"
            placeholder="e.g. 18"
          />
          <p className="text-[10px] text-[#5C6B61] mt-1">The number of physical tables your host stand seats parties at (any size). This enables the tap-to-seat / tap-to-clear board on your waitlist and starts building your real turn-time average.</p>
        </div>
        <div><label className="overline block mb-1">SMS ready template</label><textarea className="input-field" rows={3} value={form.ready_message_template} onChange={(e) => setForm({ ...form, ready_message_template: e.target.value })} /><p className="text-xs text-[#5C6B61] mt-1">Use {"{name}"} and {"{business}"} as placeholders.</p></div>
        <div><label className="overline block mb-1">AI receptionist greeting</label><textarea className="input-field" rows={3} value={form.ai_greeting} onChange={(e) => setForm({ ...form, ai_greeting: e.target.value })} /></div>

        <div className="border-t border-[#E1DFD5] pt-4">
          <div className="overline mb-2">Mimi's voice</div>
          <p className="text-xs text-[#5C6B61] leading-snug mb-3">
            Pick the voice Mimi uses on the phone and in previews. Tap <b>Preview</b> to hear each one before you save. First preview takes ~4 seconds to generate; every one after is instant.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="mimi-voice-picker">
            {[
              { v: "nova", label: "Nova", desc: "Energetic, upbeat — great for busy, bright restaurants." },
              { v: "shimmer", label: "Shimmer", desc: "Bright & cheerful — good for cafés, breakfast spots, family places." },
              { v: "coral", label: "Coral", desc: "Warm & friendly — fits fine-dining and neighborhood restaurants." },
            ].map(({ v, label, desc }) => {
              const selected = (form.mimi_voice || "nova") === v;
              return (
                <label
                  key={v}
                  className={`paper-card p-4 cursor-pointer border-2 ${selected ? "border-[#1E3A2F]" : "border-transparent"}`}
                  data-testid={`mimi-voice-${v}`}
                >
                  <div className="flex items-start gap-2">
                    <input
                      type="radio"
                      name="mimi_voice"
                      value={v}
                      checked={selected}
                      onChange={() => setForm({ ...form, mimi_voice: v })}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-heading font-extrabold text-sm">{label}</div>
                      <p className="text-xs text-[#5C6B61] leading-snug">{desc}</p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const key = `mimi_preview_${v}`;
                          if (window[key]) { window[key].pause(); delete window[key]; return; }
                          const backend = process.env.REACT_APP_BACKEND_URL || "";
                          const a = new Audio(`${backend}/api/voice/preview.mp3?voice=${v}`);
                          window[key] = a;
                          a.onended = () => { delete window[key]; };
                          a.play().catch(() => { delete window[key]; });
                        }}
                        className="mt-2 text-xs font-semibold text-[#1E3A2F] underline"
                        data-testid={`mimi-voice-preview-${v}`}
                      >
                        ▶ Preview
                      </button>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="border-t border-[#E1DFD5] pt-4">
          <div className="overline mb-2">Voice AI mode</div>
          <div className="grid grid-cols-2 gap-3">
            <label className={`paper-card p-4 cursor-pointer border-2 ${form.voice_mode === "gather" ? "border-[#1E3A2F]" : "border-transparent"}`}>
              <input type="radio" name="voice_mode" value="gather" checked={form.voice_mode === "gather"} onChange={() => setForm({ ...form, voice_mode: "gather" })} className="mr-2" data-testid="voice-mode-gather" />
              <b>Turn-based</b><p className="text-xs text-[#5C6B61] mt-1">Caller speaks, AI listens, then responds. Reliable, works everywhere.</p>
            </label>
            <label className={`paper-card p-4 cursor-pointer border-2 ${form.voice_mode === "realtime" ? "border-[#1E3A2F]" : "border-transparent"}`}>
              <input type="radio" name="voice_mode" value="realtime" checked={form.voice_mode === "realtime"} onChange={() => setForm({ ...form, voice_mode: "realtime" })} className="mr-2" data-testid="voice-mode-realtime" />
              <b>Realtime</b> <span className="tag tag-notified text-xs">Bidirectional</span><p className="text-xs text-[#5C6B61] mt-1">Callers can interrupt; AI listens while it talks. Requires an OPENAI_API_KEY in your backend .env.</p>
            </label>
          </div>
        </div>

        <div className="border-t border-[#E1DFD5] pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="overline">Operator directory</div>
              <p className="text-sm text-[#5C6B61] mt-1">Show your business on BuzzerAI's public "built with" wall. Opt-in only, remove anytime.</p>
            </div>
            <label className="inline-flex items-center cursor-pointer">
              <input type="checkbox" className="peer sr-only" checked={form.directory_optin} onChange={(e) => setForm({ ...form, directory_optin: e.target.checked })} data-testid="directory-optin" />
              <span className="w-11 h-6 bg-[#E6E2D6] rounded-full peer-checked:bg-[#1E3A2F] relative transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5"></span>
            </label>
          </div>
          {form.directory_optin && (
            <div className="space-y-3 mt-3">
              <div><label className="overline block mb-1">City (shown on directory)</label><input className="input-field" placeholder="Brooklyn, NY" value={form.directory_city} onChange={(e) => setForm({ ...form, directory_city: e.target.value })} data-testid="directory-city" /></div>
              <div><label className="overline block mb-1">Short blurb (1–2 sentences)</label><textarea rows={2} className="input-field" placeholder="Neighborhood coffee bar since 2019. Espresso and pastries."
                value={form.directory_blurb} onChange={(e) => setForm({ ...form, directory_blurb: e.target.value })} data-testid="directory-blurb" /></div>
            </div>
          )}
        </div>

        <div className="border-t border-[#E1DFD5] pt-4">
          <div className="overline mb-2">Waitlist availability</div>
          <p className="text-xs text-[#5C6B61] leading-snug mb-3">
            Control when guests can join through the public /join page. In <b>Auto</b>, the waitlist auto-closes when it hits capacity or when there isn't enough time before you close to seat one more party.
          </p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[
              ["auto", "Auto"],
              ["open", "Force open"],
              ["closed", "Closed"],
            ].map(([v, l]) => (
              <label key={v} className={`paper-card p-2 cursor-pointer border-2 text-center ${(form.waitlist_manual_status || "auto") === v ? "border-[#1E3A2F]" : "border-transparent"}`}>
                <input type="radio" name="waitlist_manual_status" value={v} checked={(form.waitlist_manual_status || "auto") === v} onChange={() => setForm({ ...form, waitlist_manual_status: v })} className="sr-only" data-testid={`waitlist-status-${v}`} />
                <div className="text-xs font-medium">{l}</div>
              </label>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <label className="overline block mb-1">Waitlist capacity</label>
              <input type="number" min={1} className="input-field" placeholder="20" value={form.waitlist_capacity || ""} onChange={(e) => setForm({ ...form, waitlist_capacity: parseInt(e.target.value || "0") || null })} data-testid="waitlist-capacity" />
              <p className="text-[10px] text-[#5C6B61] mt-1">Max parties on the list at once.</p>
            </div>
            <div>
              <label className="overline block mb-1">Open time</label>
              <input type="time" className="input-field" value={form.open_time || ""} onChange={(e) => setForm({ ...form, open_time: e.target.value })} data-testid="waitlist-open-time" />
              <p className="text-[10px] text-[#5C6B61] mt-1">When your doors open (local time).</p>
            </div>
            <div>
              <label className="overline block mb-1">Kitchen close time</label>
              <input type="time" className="input-field" value={form.close_time || ""} onChange={(e) => setForm({ ...form, close_time: e.target.value })} data-testid="waitlist-close-time" />
              <p className="text-[10px] text-[#5C6B61] mt-1">Auto-closes list + auto-quiets manager after this.</p>
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs text-[#121914]">
            <input type="checkbox" checked={form.standby_enabled !== false} onChange={(e) => setForm({ ...form, standby_enabled: e.target.checked })} className="mt-0.5" data-testid="waitlist-standby-enabled" />
            <span>Let guests join a <b>standby list</b> when full — they auto-promote when a party cancels or no-shows.</span>
          </label>
        </div>

        <div className="border-t border-[#E1DFD5] pt-4">
          <div className="overline mb-2">Manager on duty</div>
          <p className="text-xs text-[#5C6B61] leading-snug mb-2">
            When a caller asks for a manager, supervisor, owner, or someone in charge, BuzzerAI will instantly forward the live call to this number. If nobody answers within 25 seconds we text the caller a callback offer.
          </p>
          <input className="input-field" placeholder="+15555550100 — mobile that always rings" value={form.manager_phone || ""} onChange={(e) => setForm({ ...form, manager_phone: e.target.value })} data-testid="manager-phone" />
          <p className="text-[10px] text-[#5C6B61] mt-1">Leave blank to fall back to the Staff phone above.</p>

          <label className="flex items-start gap-2 text-xs text-[#121914] mt-3 p-3 rounded-lg bg-[#C2D1C1]/25 border border-[#1E3A2F]/15" data-testid="manager-off-hours-block">
            <input
              type="checkbox"
              checked={form.manager_off_hours_auto !== false}
              onChange={(e) => setForm({ ...form, manager_off_hours_auto: e.target.checked })}
              className="mt-0.5"
              data-testid="manager-off-hours-auto"
            />
            <span>
              <b>Auto-quiet the manager line outside of business hours.</b> When on, Mimi captures a callback and tells the guest we'll ring them back when we open (using the open/close times above). Turn off if you want the manager phone to ring 24/7.
            </span>
          </label>

          <div className="mt-3 p-3 rounded-lg bg-[#FEF3C7] border border-[#F5C543]/40" data-testid="emergency-close-block">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <div className="overline text-[#92400E]">Emergency close</div>
                <p className="text-[11px] text-[#78350F] leading-snug mt-0.5">
                  {form.emergency_closed_until
                    ? <>Currently closed until <b>{new Date(form.emergency_closed_until).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</b>.</>
                    : <>Close early for a burst pipe, power outage, or private event. Overrides business hours until you clear it or the time passes.</>
                  }
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { hrs: 2, label: "2 hours" },
                { hrs: 4, label: "Rest of tonight" },
                { hrs: 24, label: "24 hours" },
              ].map((opt) => (
                <button
                  key={opt.hrs}
                  type="button"
                  onClick={() => {
                    const iso = new Date(Date.now() + opt.hrs * 3600 * 1000).toISOString();
                    setForm({ ...form, emergency_closed_until: iso });
                  }}
                  className="text-xs bg-white hover:bg-[#FEF3C7] border border-[#F5C543]/60 rounded-full px-3 py-1.5 font-semibold text-[#78350F]"
                  data-testid={`emergency-close-${opt.hrs}h`}
                >
                  Close for {opt.label}
                </button>
              ))}
              {form.emergency_closed_until && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, emergency_closed_until: "" })}
                  className="text-xs bg-[#1E3A2F] text-white rounded-full px-3 py-1.5 font-semibold"
                  data-testid="emergency-close-clear"
                >
                  Reopen now
                </button>
              )}
            </div>
          </div>

          <div className="mt-3">
            <div className="overline mb-1">Per-day override (optional)</div>
            <p className="text-[10px] text-[#5C6B61] mb-2">Different manager on different days? Enter their mobile per weekday. Blank rows use the default above.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[["mon","Mon"],["tue","Tue"],["wed","Wed"],["thu","Thu"],["fri","Fri"],["sat","Sat"],["sun","Sun"]].map(([k,lbl]) => (
                <div key={k} className="flex items-center gap-1">
                  <span className="text-[10px] w-8 text-[#5C6B61]">{lbl}</span>
                  <input
                    className="input-field !py-1 !px-2 text-xs flex-1"
                    placeholder="+1…"
                    value={(form.manager_schedule || {})[k] || ""}
                    onChange={(e) => setForm({ ...form, manager_schedule: { ...(form.manager_schedule || {}), [k]: e.target.value } })}
                    data-testid={`manager-schedule-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-[#E1DFD5] pt-4">
          <div className="overline mb-2">To-go orders</div>
          <p className="text-xs text-[#5C6B61] leading-snug mb-3">In-app to-go orders are on the way. Until then, if you already have a self-service ordering page (Toast Order, Square Online, ChowNow, your own site), paste the link below and the AI will offer to <b>text it to any caller who asks about to-go</b> — no more phone-order interruptions during service.</p>
          <label className="block text-xs font-semibold text-[#121914] mb-1.5">Your self-service to-go link</label>
          <input
            type="url"
            className="input w-full"
            placeholder="https://yourrestaurant.com/order-online"
            value={form.togo_url || ""}
            onChange={(e) => setForm({ ...form, togo_url: e.target.value })}
            data-testid="settings-togo-url"
          />
          <p className="text-xs text-[#5C6B61] mt-2 leading-snug">
            When set, the AI receptionist offers callers a choice: text them the link, or transfer to a live person for a phone order. Leave blank and every to-go call routes to a person by default.
          </p>
        </div>

        <EmbedSnippet business={business} />

        <button className="btn-primary" data-testid="settings-save">Save changes</button>
      </form>
    </div>
  );
}

function EmbedSnippet({ business }) {
  const origin = window.location.origin;
  const snippet = `<script src="${origin}/embed.js" data-slug="${business.slug}" async></script>`;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="border-t border-[#E1DFD5] pt-4" data-testid="embed-snippet-section">
      <div className="overline mb-2">Add chat to your website</div>
      <p className="text-xs text-[#5C6B61] leading-snug mb-3">
        Paste this one line before <code className="bg-[#F7F6F2] px-1 rounded">&lt;/body&gt;</code> on your Squarespace, Wix, Shopify, Wordpress, or custom site. A floating chat button appears bottom-right — guests can join the waitlist or ask a question without leaving your homepage. Requires the AI Chat Receptionist add-on.
      </p>
      <div className="paper-card p-3 bg-[#121914] text-[#C2D1C1] font-mono text-[11px] leading-snug break-all mb-2" data-testid="embed-snippet-code">
        {snippet}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={copy} className="btn-primary !py-1.5 !px-3 text-xs" data-testid="embed-snippet-copy">
          {copied ? "Copied!" : "Copy snippet"}
        </button>
        <a href={`/embed/${business.slug}`} target="_blank" rel="noreferrer" className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="embed-snippet-preview">
          Preview live
        </a>
      </div>
      <p className="text-[11px] text-[#5C6B61] mt-2 leading-snug">
        The widget inherits your business name and greeting. To hide it later, delete the &lt;script&gt; line.
      </p>
    </div>
  );
}
